import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../lib/api';
import { formatPos } from '../lib/format';
import { ConfirmDialog } from '../ui/dialog';
import { ErrorNotice, useToast } from '../ui/feedback';
import { Menu } from '../ui/menu';
import { DataTable, PageHead, Tabs } from '../ui/table';
import { DefList, ItemId, Mono, Num, Tag, Time, Uuid } from '../ui/values';
import { AreaLabel, BackLink } from './common';
import { paths } from './paths';
import type { AdminPlan } from './types';

/**
 * Sortierplan (Revision R5): Kopfdaten, Zuordnung Kiste → Gruppe in Plan-Reihenfolge, Gruppen und Zusammenlegungen.
 * Der Plan ist eine Empfehlung der Mod; Admins können ihn nur ansehen und löschen (mit Grund).
 */
export function SortPlanPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'assignments' | 'groups' | 'merges'>('assignments');
  const [deleting, setDeleting] = useState(false);
  const q = useQuery({ queryKey: ['admin', 'sort-plan', id], queryFn: () => api.get<AdminPlan>(`/admin/sort-plans/${id}`) });
  const p = q.data;

  if (q.isError) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (!p) return <p className="muted">Lädt…</p>;

  const scopePath = p.scope.type === 'team' ? paths.team(p.scope.id) : paths.player(p.scope.id);
  const groupName = new Map(p.groups.map((g) => [g.id, g.name]));
  const chests = new Map(p.chests.map((c) => [c.id, c]));
  const targets = new Set(p.targets);
  const rows = p.assignments.map((a, i) => ({ ...a, n: i + 1, chestInfo: chests.get(a.chest) }));
  const label = (group: string) => groupName.get(group) ?? group;

  return (
    <div className="stack-lg">
      <PageHead
        back={<BackLink to={scopePath} label={p.scope.type === 'team' ? 'Team' : 'Spieler'} />}
        title={
          <>
            Sortierplan <AreaLabel name={p.area} />
          </>
        }
        context={
          <>
            <span className="mono">{p.server}</span> · <span className="mono">{p.dimension}</span>
          </>
        }
        actions={<Menu items={[{ label: 'Plan löschen…', danger: true, onSelect: () => setDeleting(true) }]} />}
      />

      <DefList
        items={[
          [p.scope.type === 'team' ? 'Team' : 'Spieler', <Link to={scopePath}>{p.scope.name ?? p.scope.id}</Link>],
          ['Area', <AreaLabel name={p.area} />],
          ['Revision', <span>{p.revision} · Layout-Version {p.layoutVersion}</span>],
          ['Kisten', <span><Num value={p.chestCount} /> zugeordnet · <Num value={p.known.length} /> bekannt{p.targets.length > 0 && <> · <Num value={p.targets.length} /> Ziele</>}</span>],
          ['Kein Platz', <span><Num value={p.unmetDemandStacks} /> Stacks</span>],
          ['Falsch einsortiert', <span><Num value={p.misplacedStacks} /> Stacks</span>],
          ['Berechnet', <Time iso={p.computedAt} absolute />],
          ['Zuletzt gespeichert', <span>{p.updatedBy?.name ?? 'unbekannt'} · <Time iso={p.updatedAt} absolute /></span>],
          ['Plan-ID', <Uuid value={p.id} />],
        ]}
      />

      <div className="stack">
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'assignments', label: 'Zuordnung', count: p.assignments.length },
            { key: 'groups', label: 'Gruppen', count: p.groups.length },
            { key: 'merges', label: 'Zusammenlegungen', count: p.merges.length },
          ]}
        />
        {tab === 'assignments' && (
          <DataTable
            label="Zuordnung"
            rows={rows}
            rowKey={(r) => r.chest}
            empty="Keine Kisten zugeordnet."
            columns={[
              { key: 'n', header: '#', num: true, cell: (r) => r.n },
              { key: 'group', header: 'Gruppe', cell: (r) => label(r.group) },
              {
                key: 'chest',
                header: 'Kiste',
                cell: (r) =>
                  !r.chestInfo ? (
                    <Tag tone="warn">unbekannt</Tag>
                  ) : r.chestInfo.deleted ? (
                    <span>
                      <span className="muted">{r.chestInfo.name ?? formatPos(r.chestInfo.pos)}</span> <Tag tone="neutral">entfernt</Tag>
                    </span>
                  ) : (
                    <Link to={paths.chest(r.chest)}>{r.chestInfo.name ?? formatPos(r.chestInfo.pos)}</Link>
                  ),
              },
              { key: 'pos', header: 'Position', hide: 'sm', cell: (r) => (r.chestInfo ? <span className="mono">{formatPos(r.chestInfo.pos)}</span> : <span className="muted">–</span>) },
              { key: 'owner', header: 'Besitzer', hide: 'md', cell: (r) => r.chestInfo?.owner ?? <span className="muted">–</span> },
              { key: 'target', header: 'Ziel', hide: 'md', cell: (r) => (targets.has(r.chest) ? <Tag tone="neutral">Ziel</Tag> : null) },
            ]}
          />
        )}
        {tab === 'groups' && (
          <DataTable
            label="Gruppen"
            rows={p.groups}
            rowKey={(g) => g.id}
            empty="Keine Gruppen."
            columns={[
              { key: 'name', header: 'Gruppe', cell: (g) => g.name },
              { key: 'id', header: 'ID', hide: 'sm', cell: (g) => <Mono value={g.id} copy={false} /> },
              { key: 'item', header: 'Einzel-Item', cell: (g) => (g.itemId ? <ItemId id={g.itemId} /> : <span className="muted">–</span>) },
            ]}
          />
        )}
        {tab === 'merges' && (
          <DataTable
            label="Zusammenlegungen"
            rows={p.merges}
            rowKey={(m) => m.from}
            empty="Keine Zusammenlegungen."
            columns={[
              { key: 'from', header: 'Gruppe', cell: (m) => label(m.from) },
              { key: 'to', header: 'teilt sich die Kiste mit', cell: (m) => label(m.to) },
            ]}
          />
        )}
      </div>

      {deleting && (
        <ConfirmDialog
          open
          title="Sortierplan löschen"
          consequence={`Löscht den Plan für „${p.area || 'Default'}“. Die Mods des Teams entfernen ihn beim nächsten Abgleich; die Kisten bleiben unverändert. Ein Teammitglied kann jederzeit einen neuen Plan berechnen.`}
          confirmLabel="Löschen"
          onConfirm={async (reason) => {
            await api.delete(`/admin/sort-plans/${id}`, { reason });
            await qc.invalidateQueries({ queryKey: ['admin', 'sort-plans'] });
            toast('Sortierplan gelöscht.');
            navigate(scopePath);
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </div>
  );
}
